import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box } from '@mui/material';
import { getTalkRooms } from '../api/talkRooms';
import { TalkRoom, TalkSearchParams } from '../types/talk';
import SearchForm from '../components/talkRoom/SearchForm';
import TalkRoomTable from '../components/talkRoom/TalkRoomTable';
import { useLocation, useNavigate } from 'react-router-dom';

const STORAGE_KEY = 'talk_list_search';
const TalkRoomList: React.FC = () => {
  const navigate = useNavigate();
  // ロケーション情報を取得（ダッシュボードからの遷移パラメータ用）
  const location = useLocation();
  const locationState = location.state as { selectedStatuses?: string[] } | null;
  // 検索条件を取得する関数
  const getSavedFilters = () => {
    try {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (savedState) {
        return JSON.parse(savedState);
      }
    } catch (e) {
      console.error('検索条件の復元に失敗:', e);
      localStorage.removeItem(STORAGE_KEY);
    }
    return null;
  };
  // 保存された状態または初期値で状態を初期化
  const savedFilters = getSavedFilters();
  const [ talkRooms, setTalkRooms ] = useState<TalkRoom[]>([]);
  const [ totalCount, setTotalCount ] = useState(0);
  const [ loading, setLoading ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);
  // リクエスト重複防止用のref
  const requestInProgressRef = useRef<{ [key: string]: boolean }>({});
  // フォーム入力値（ユーザーの入力を保持）
  const [ nurseSearchTerm, setNurseSearchTerm ] = useState(savedFilters?.nurseSearchTerm || '');
  const [ patientSearchTerm, setPatientSearchTerm ] = useState(savedFilters?.patientSearchTerm || '');
  const [ selectedStatuses, setSelectedStatuses ] = useState<string[]>(locationState?.selectedStatuses || savedFilters?.selectedStatuses || []);
  const [ selectedPrices, setSelectedPrices ] = useState<number[]>(savedFilters?.selectedPrices || []);
  // 実際の検索条件（検索ボタン押下時に更新）
  const [ searchParams, setSearchParams ] = useState({
    nurseSearchTerm: savedFilters?.nurseSearchTerm || '',
    patientSearchTerm: savedFilters?.patientSearchTerm || '',
    selectedStatuses: locationState?.selectedStatuses || savedFilters?.selectedStatuses || [] as string[],
    selectedPrices: savedFilters?.selectedPrices || [] as number[]
  });
  // ページネーション用の状態
  const [ page, setPage ] = useState(savedFilters?.page || 0);
  const [ rowsPerPage, setRowsPerPage ] = useState(savedFilters?.rowsPerPage || 20);
  // ソート用の状態（複数フィールド対応）
  const [ sortFields, setSortFields ] = useState<Array<{ field: string; direction: 'asc' | 'desc' }>>(
    savedFilters?.sortFields || [ { field: 'id', direction: 'desc' } ]
  );
  // 対話データを取得する関数
  const fetchTalkRooms = async(params: TalkSearchParams) => {
    // APIパラメータのキャッシュキーを作成
    const cacheKey = JSON.stringify(params);
    // 同じパラメータでのリクエストが進行中であれば、新しいリクエストを送信しない
    if (requestInProgressRef.current[cacheKey]) {
      return;
    }
    // リクエスト開始をマーク
    requestInProgressRef.current[cacheKey] = true;
    setLoading(true);
    setError(null);
    try {
      // クエリパラメータを加工
      const apiParams = {
        ...params,
        page: params.page ? params.page + 1 : 1, // APIは1-indexedだがMUIは0-indexed
        page_size: params.page_size,
        ordering: params.ordering // ソート順を追加
      };
      const data = await getTalkRooms(apiParams);
      // APIからの結果をセット
      if (data && data.results) {
        setTalkRooms(data.results);
        setTotalCount(data.count);
      } else {
        // API結果がない場合は空の配列をセット
        setTalkRooms([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('対話データの取得に失敗しました', err);
      setError('対話データの取得に失敗しました。API接続を確認してください。');
      setTalkRooms([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      // リクエスト完了をマーク
      delete requestInProgressRef.current[cacheKey];
    }
  };
  // 検索実行関数
  const handleSearch = () => {
    // 検索条件を更新
    setSearchParams({
      nurseSearchTerm,
      patientSearchTerm,
      selectedStatuses,
      selectedPrices
    });
    setPage(0); // ページをリセット
  };
  // 検索条件をクリア
  const handleClearFilters = () => {
    setNurseSearchTerm('');
    setPatientSearchTerm('');
    setSelectedStatuses([]);
    setSelectedPrices([]);
    // 検索条件も即時クリア
    setSearchParams({
      nurseSearchTerm: '',
      patientSearchTerm: '',
      selectedStatuses: [],
      selectedPrices: []
    });
    setPage(0);
    // ソート順もデフォルトに戻す
    setSortFields([ { field: 'id', direction: 'desc' } ]);
    localStorage.removeItem(STORAGE_KEY); // ローカルストレージからも削除
  };
  // ダッシュボードからの遷移をチェックして検索条件をリセット
  useEffect(() => {
    if (locationState?.selectedStatuses) {
      // ダッシュボードからの遷移の場合、すべての検索条件をリセットしてstatusのみ設定
      setNurseSearchTerm('');
      setPatientSearchTerm('');
      setSelectedStatuses(locationState.selectedStatuses);
      setSelectedPrices([]);
      setSearchParams({
        nurseSearchTerm: '',
        patientSearchTerm: '',
        selectedStatuses: locationState.selectedStatuses,
        selectedPrices: []
      });
      setPage(0);
      // ローカルストレージもクリア
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []); // 初回のみ実行
  // 検索条件が変更されたときにローカルストレージに保存し、データ取得を行う
  useEffect(() => {
    // マウント状態管理
    let isMounted = true;
    // ローカルストレージに保存
    const currentState = {
      nurseSearchTerm: searchParams.nurseSearchTerm,
      patientSearchTerm: searchParams.patientSearchTerm,
      selectedStatuses: searchParams.selectedStatuses,
      selectedPrices: searchParams.selectedPrices,
      page,
      rowsPerPage,
      sortFields
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(currentState));
    // API検索パラメータの構築
    const params: TalkSearchParams = {
      page,
      page_size: rowsPerPage,
      nurse_search: searchParams.nurseSearchTerm || undefined,
      patient_search: searchParams.patientSearchTerm || undefined,
      ordering: sortFields.map(sf => sf.direction === 'desc' ? `-${sf.field}` : sf.field).join(',')
    };
    // 選択されているステータスがある場合のみパラメータに追加
    if (searchParams.selectedStatuses.length > 0) {
      params.talk_status = searchParams.selectedStatuses;
    }
    // 選択されている価格がある場合のみパラメータに追加
    if (searchParams.selectedPrices.length > 0) {
      params.price = searchParams.selectedPrices;
    }
    // 遅延実行によりレンダリングと状態更新が完全に完了してから取得
    const timerId = setTimeout(() => {
      if (isMounted) {
        fetchTalkRooms(params);
      }
    }, 10);
    // クリーンアップ関数
    return () => {
      isMounted = false;
      clearTimeout(timerId);
    };
  }, [ page, rowsPerPage, searchParams, sortFields ]);
  // ページ変更ハンドラ
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };
  // 1ページあたりの表示件数変更ハンドラ
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  // 行クリック時のハンドラ
  const handleRowClick = (nurseId: number, patientId: number) => {
    // 前回のページネーションパラメータをクリア
    sessionStorage.removeItem('talkRoomSummary_nurseId');
    sessionStorage.removeItem('talkRoomSummary_patientId');
    navigate(`/talk_rooms/summary?nurse_id=${nurseId}&patient_id=${patientId}`);
  };
  // ソート順変更ハンドラ（クリックされた順に管理）
  const handleRequestSort = (property: string) => {
    setSortFields(prevFields => {
      // 既存のフィールドを探す
      const existingIndex = prevFields.findIndex(sf => sf.field === property);
      if (existingIndex >= 0) {
        // 既に存在する場合は方向を反転
        const newFields = [ ...prevFields ];
        newFields[existingIndex] = {
          ...newFields[existingIndex],
          direction: newFields[existingIndex].direction === 'asc' ? 'desc' : 'asc'
        };
        return newFields;
      } else {
        // 新しいフィールドを追加（初回クリック時はidを削除）
        const newFields = prevFields.filter(sf => sf.field !== 'id');
        return [ ...newFields, { field: property, direction: 'desc' } ];
      }
    });
  };
  return (
    <Box>
      <SearchForm
        nurseSearchTerm={nurseSearchTerm}
        patientSearchTerm={patientSearchTerm}
        selectedStatuses={selectedStatuses}
        selectedPrices={selectedPrices}
        totalCount={totalCount}
        onNurseSearchChange={setNurseSearchTerm}
        onPatientSearchChange={setPatientSearchTerm}
        onStatusChange={setSelectedStatuses}
        onPriceChange={setSelectedPrices}
        onSearch={handleSearch}
        onClearFilters={handleClearFilters}
        onClearNurseSearch={() => setNurseSearchTerm('')}
        onClearPatientSearch={() => setPatientSearchTerm('')}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TalkRoomTable
        talkRooms={talkRooms}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        loading={loading}
        sortFields={sortFields}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onRowClick={handleRowClick}
        onRequestSort={handleRequestSort}
      />
    </Box>
  );
};
export default TalkRoomList;
